// views/_preview/DashboardPreview.tsx
//
// DEMO / SIMULÁTOR. Zobrazí skutečný dashboard bez backendu a přihlášení.
// Podkládá AuthContext mock uživatelem a stubuje datovou vrstvu (Entities)
// demo daty, takže návštěvník může klikat po celém dashboardu — bonusy,
// detail bonusu, historie, doporučení i nastavení.
//
// Používá se:
//  - na route /preview/dashboard (celostránkové demo)
//  - v iframu telefonu na HomePage (interaktivní simulátor pro klienty)
//
// Stuby žijí jen v JS kontextu okna, ve kterém se tato komponenta mountne
// (v iframu tedy neovlivní hlavní aplikaci).
import { useEffect, useMemo, type ReactNode } from 'react';
import { AuthContext } from '../../contexts/AuthContext';
import {
  Bonus,
  BonusHistoryObj,
  BonusProgress,
  BonusType,
  Order,
  Referenced,
  ReferralCode,
  Risk,
  SpecialMark,
  Tier,
  Transaction,
  User,
} from '../../Entities';

const MONTHS = ['Led', 'Úno', 'Bře', 'Dub', 'Kvě', 'Čer'];
const now = Math.floor(Date.now() / 1000);
const DAY = 24 * 3600;

// ── Grafová data ─────────────────────────────────────────────
const SAMPLE_BONUS_STATS = MONTHS.map((month, i) => ({
  month,
  reached_profit: [4200, 5100, 3800, 6300, 5600, 7100][i],
  success_rate: [0.62, 0.71, 0.55, 0.78, 0.69, 0.83][i],
  bonuses: [12, 15, 9, 18, 14, 21][i],
}));
const SAMPLE_TX_STATS = MONTHS.map((month, i) => ({
  month,
  reached_profit: [600, 900, 700, 1100, 850, 1300][i],
}));

// ── Konstanty (jinak se načítají z backendu po přihlášení) ───
const DEMO_BONUS_TYPES = [
  { id: 1, name: 'registration', label: 'Registrační', color: '#3b82f6', background_color: '#3b82f620', icon: '📝' },
  { id: 2, name: 'slot', label: 'Automaty', color: '#e37627', background_color: '#e3762720', icon: '🎰' },
  { id: 3, name: 'live', label: 'Živé hry', color: '#8b5cf6', background_color: '#8b5cf620', icon: '🎲' },
  { id: 4, name: 'bank', label: 'Bankovní', color: '#10b981', background_color: '#10b98120', icon: '🏦' },
];
const DEMO_RISKS = [
  { id: 1, rate: 'free', label: 'Bez rizika', index: 0, color: '#3b82f6', color_not_clicked: '#b6c8e6', icon: '🛡️' },
  { id: 2, rate: 'low', label: 'Nízké riziko', index: 1, color: '#10b981', color_not_clicked: '#b4dcc9', icon: '🌿' },
  { id: 3, rate: 'medium', label: 'Střední riziko', index: 2, color: '#eab308', color_not_clicked: '#e8dcae', icon: '⚖️' },
  { id: 4, rate: 'high', label: 'Vysoké riziko', index: 3, color: '#f97316', color_not_clicked: '#eccbb2', icon: '🔥' },
  { id: 5, rate: 'extreme', label: 'Extrémní riziko', index: 4, color: '#ef4444', color_not_clicked: '#e9bcbc', icon: '💥' },
];
const DEMO_MARKS = [
  { id: 1, name: 'VIP', label: 'VIP', color: '#d97706' },
  { id: 2, name: 'recommended', label: 'Doporučený', color: '#e37627' },
  { id: 3, name: 'exceptional', label: 'Výjimečný', color: '#a855f7' },
];

// ── Demo bonusy ──────────────────────────────────────────────
// Každý typ bonusu má vlastní kroky — bankovní bonus nemá „Protočení“ apod.
const CASINO_STEPS = [
  {
    title: 'Registrace',
    subtitle: 'Založte si účet u kasina přes náš odkaz.',
    icon: '📝',
    description: '<p>Klikněte na tlačítko „Přejít na stránku“ a vyplňte registrační formulář. Použijte své skutečné údaje — kasino je ověřuje.</p>',
  },
  {
    title: 'Vklad',
    subtitle: 'Vložte potřebný kapitál na herní účet.',
    icon: '💳',
    description: '<p>Vložte uvedený potřebný kapitál. Doporučujeme platbu kartou — je okamžitá.</p>',
  },
  {
    title: 'Protočení',
    subtitle: 'Protočte bonus podle instrukcí.',
    icon: '🎰',
    description: '<p>Hrajte doporučenou hru s uvedenou sázkou, dokud nesplníte podmínky protočení bonusu.</p>',
  },
  {
    title: 'Výběr',
    subtitle: 'Vyberte peníze zpět na svůj účet.',
    icon: '💰',
    description: '<p>Po splnění podmínek si nechte zůstatek vyplatit zpět na svůj bankovní účet.</p>',
  },
];

const BANK_STEPS = [
  {
    title: 'Založení účtu',
    subtitle: 'Založte si nový účet u banky přes náš odkaz.',
    icon: '📝',
    description: '<p>Klikněte na tlačítko „Přejít na stránku“ a projděte online založením účtu. Budete potřebovat občanský průkaz a pár minut času.</p>',
  },
  {
    title: 'Aktivace',
    subtitle: 'Aktivujte účet a nastavte si mobilní aplikaci.',
    icon: '📲',
    description: '<p>Přihlaste se do bankovnictví, dokončete aktivaci a nainstalujte si mobilní aplikaci banky.</p>',
  },
  {
    title: 'Splnění podmínek',
    subtitle: 'Splňte podmínky odměny — např. platby kartou.',
    icon: '✅',
    description: '<p>Splňte podmínky uvedené v akci — typicky několik plateb kartou nebo příchozí platba na účet. Vše bez rizika.</p>',
  },
  {
    title: 'Odměna',
    subtitle: 'Banka vám připíše bonus na účet.',
    icon: '💰',
    description: '<p>Po splnění podmínek vám banka odměnu automaticky připíše. Peníze si můžete kdykoliv převést na svůj hlavní účet.</p>',
  },
];

const RELOAD_STEPS = [
  {
    title: 'Vklad',
    subtitle: 'Vložte vklad, kterým aktivujete reload bonus.',
    icon: '💳',
    description: '<p>Přihlaste se ke svému stávajícímu účtu a vložte uvedený vklad. Bonus se aktivuje automaticky.</p>',
  },
  {
    title: 'Aktivace bonusu',
    subtitle: 'Potvrďte bonus v sekci nabídek.',
    icon: '🎁',
    description: '<p>V sekci „Moje nabídky“ potvrďte aktivaci reload bonusu ještě před první hrou — jinak propadne.</p>',
  },
  {
    title: 'Protočení',
    subtitle: 'Protočte bonus podle naší strategie.',
    icon: '🎲',
    description: '<p>Hrajte doporučenou hru s uvedenou sázkou přesně podle instrukcí, dokud nesplníte podmínky protočení.</p>',
  },
  {
    title: 'Výběr',
    subtitle: 'Vyberte zůstatek zpět na svůj účet.',
    icon: '💰',
    description: '<p>Po splnění podmínek si nechte zůstatek vyplatit zpět na svůj bankovní účet.</p>',
  },
];

const SPINS_STEPS = [
  {
    title: 'Registrace',
    subtitle: 'Založte si účet přes náš odkaz.',
    icon: '📝',
    description: '<p>Klikněte na tlačítko „Přejít na stránku“ a vyplňte registrační formulář. Free spiny dostanete jen za registraci — bez vkladu.</p>',
  },
  {
    title: 'Aktivace spinů',
    subtitle: 'Aktivujte volná zatočení ve svém profilu.',
    icon: '🎡',
    description: '<p>Po ověření účtu najdete free spiny ve svém profilu. Aktivujte je — pozor, mívají omezenou platnost.</p>',
  },
  {
    title: 'Protočení výher',
    subtitle: 'Protočte výhry ze spinů podle instrukcí.',
    icon: '🎰',
    description: '<p>Výhry ze spinů je potřeba protočit podle podmínek. Hrajte doporučenou hru s minimální sázkou.</p>',
  },
  {
    title: 'Výběr',
    subtitle: 'Vyberte výhru na svůj účet.',
    icon: '💰',
    description: '<p>Po splnění podmínek si nechte výhru vyplatit zpět na svůj bankovní účet.</p>',
  },
];

const DEMO_BONUSES = [
  {
    id: 1,
    name: 'Vstupní bonus 100 %',
    description: 'Kasino nabízí zdvojnásobení prvního vkladu. S naší strategií z něj vytěžíte průměrně 620 Kč.',
    bonus_type_name: 'slot',
    risk_rate: 'low',
    special_mark: 'recommended',
    expected_value: 620,
    needed_capital: 1000,
    start_timestamp: now - 2 * DAY,
    end_timestamp: now + 5 * DAY,
    step_idx: 0,
    start_balance: null,
    step_titles: CASINO_STEPS.map((s) => s.title),
    step_list: CASINO_STEPS,
    games: [],
    company: { id: 1, name: 'Demo Casino' },
    url_bonus_conditions: '#',
    url_link: '#',
  },
  {
    id: 2,
    name: 'Bonus za účet zdarma',
    description: 'Založení bankovního účtu s odměnou. Bez rizika — stačí splnit podmínky používání.',
    bonus_type_name: 'bank',
    risk_rate: 'free',
    special_mark: null,
    expected_value: 1500,
    needed_capital: 0,
    start_timestamp: now - 10 * DAY,
    end_timestamp: now + 20 * DAY,
    step_idx: 1,
    start_balance: 0,
    step_titles: BANK_STEPS.map((s) => s.title),
    step_list: BANK_STEPS,
    games: [],
    company: { id: 2, name: 'Demo Banka' },
    url_bonus_conditions: '#',
    url_link: '#',
  },
  {
    id: 3,
    name: 'VIP reload pátek',
    description: 'Páteční reload bonus pro aktivní hráče. Vyšší zisk za cenu vyššího rizika.',
    bonus_type_name: 'live',
    risk_rate: 'high',
    special_mark: 'VIP',
    expected_value: 2400,
    needed_capital: 5000,
    start_timestamp: now - DAY,
    end_timestamp: now + 2 * DAY,
    step_idx: 0,
    start_balance: 500,
    step_titles: RELOAD_STEPS.map((s) => s.title),
    step_list: RELOAD_STEPS,
    games: [],
    company: { id: 3, name: 'Demo Live' },
    url_bonus_conditions: '#',
    url_link: '#',
  },
  {
    id: 4,
    name: 'Registrační free spiny',
    description: 'Volná zatočení jen za registraci. Ideální první bonus na vyzkoušení.',
    bonus_type_name: 'registration',
    risk_rate: 'free',
    special_mark: 'exceptional',
    expected_value: 250,
    needed_capital: 0,
    start_timestamp: now - 3 * DAY,
    end_timestamp: now + 12 * DAY,
    step_idx: 0,
    start_balance: 0,
    step_titles: SPINS_STEPS.map((s) => s.title),
    step_list: SPINS_STEPS,
    games: [],
    company: { id: 4, name: 'Demo Spiny' },
    url_bonus_conditions: '#',
    url_link: '#',
  },
];

const DEMO_HISTORY = [
  { id: 1, reached_profit: 620, creation_timestamp: now - 3 * DAY, bonus: { name: 'Vstupní bonus 100 %', bonus_type: 'slot', expected_value: 620, company: { name: 'Demo Casino' } } },
  { id: 2, reached_profit: 1480, creation_timestamp: now - 9 * DAY, bonus: { name: 'Bonus za účet zdarma', bonus_type: 'bank', expected_value: 1500, company: { name: 'Demo Banka' } } },
  { id: 3, reached_profit: 310, creation_timestamp: now - 15 * DAY, bonus: { name: 'Registrační free spiny', bonus_type: 'registration', expected_value: 250, company: { name: 'Demo Spiny' } } },
  { id: 4, reached_profit: 2150, creation_timestamp: now - 22 * DAY, bonus: { name: 'VIP reload pátek', bonus_type: 'live', expected_value: 2400, company: { name: 'Demo Live' } } },
];

const DEMO_TIERS = [
  { id: 1, name: 'Free', subtitle: 'na vyzkoušení', price: 0, max_return: 0, in_use: false, free_trial_days: 0, features: ['Přehled výdělků'] },
  { id: 2, name: 'Basic', subtitle: 'pro nováčky', price: 1290, max_return: 12000, in_use: true, free_trial_days: 30, features: ['Denní aktualizace', 'Bonusy s nižším rizikem', 'Cca 150 bonusů měsíčně', 'referral reward 10 %'] },
  { id: 3, name: 'Premium', subtitle: 'pro náročné klienty', price: 1590, max_return: 25000, in_use: true, free_trial_days: 0, features: ['Denní aktualizace', 'Všechny bonusy', 'Speciální bonusy navíc', 'referral reward 10 %'] },
];

const DEMO_REFERENCED = [
  { name: 'honza_92', tier_name: 'Basic', creation_timestamp: now - 40 * DAY, referencer_reward: 129 },
  { name: 'marketa.k', tier_name: 'Premium', creation_timestamp: now - 21 * DAY, referencer_reward: 159 },
  { name: 'petr_hraje', tier_name: 'Basic', creation_timestamp: now - 6 * DAY, referencer_reward: 129 },
];

const mockUser = {
  id: 1,
  name: 'Demo Uživatel',
  full_name: 'Demo Uživatel',
  email: 'demo@digibrigi.cz',
  tier: { name: 'Basic', creation_timestamp: now - 60 * DAY },
  admin: false,
  statistician: false,
  bonus_revenue: 18450,
  referral_revenue: 3200,
  potential_bonus_revenue: 24000,
  bonus_history_len: 42,
  balance: 1280,
  paid_out: 5400,
  bank_account: '123456789/0800',
  auto_withdraw: false,
  active_order: { amount: 1290, creation_timestamp: now - 10 * DAY, expiration_timestamp: now + 20 * DAY, last_paid_amount: 1290 },
  provider_id: 'password',
  used_free_trial: true,
  professional: false,
  referral_code: 'DEMO123',
  journey_done_step_ids: [],
};

const noop = () => {};

/** Nainstaluje všechny stuby datové vrstvy (idempotentní, jen v tomto okně). */
const installStubs = () => {
  // Konstanty
  (BonusType as any).values = DEMO_BONUS_TYPES.map((v) => new (BonusType as any)(v));
  (Risk as any).values = DEMO_RISKS.map((v) => new (Risk as any)(v));
  (SpecialMark as any).values = DEMO_MARKS.map((v) => new (SpecialMark as any)(v));

  // Statistiky / grafy
  (BonusHistoryObj as any).getStats = async () => SAMPLE_BONUS_STATS;
  (Transaction as any).getStats = async () => SAMPLE_TX_STATS;
  (User as any).getStats = async () => ({ total_users: 18, total_made: 1897214, avg_month_revenue: 17468 });

  // Bonusy
  (Bonus as any).getAll = async () => DEMO_BONUSES.map((b) => new (Bonus as any)(b));
  (Bonus as any).prototype.getSteps = async function () {
    this.step_list = this.step_list ?? CASINO_STEPS;
    return this.step_list;
  };
  (Bonus as any).prototype.hide = async () => {};
  (BonusProgress as any).updateStepIdx = async () => {};
  (BonusProgress as any).updateStartBalance = async () => {};

  // Historie bonusů
  (BonusHistoryObj as any).getAll = async () => DEMO_HISTORY.map((h) => new (BonusHistoryObj as any)(h));
  (BonusHistoryObj as any).create = async () => {};
  (BonusHistoryObj as any).prototype.update = async () => {};
  (BonusHistoryObj as any).prototype.delete = async () => {};

  // Doporučení
  (Referenced as any).getAll = async () => DEMO_REFERENCED;
  (ReferralCode as any).getAll = async () => [{ id: 1, code: 'DEMO123' }];
  (Transaction as any).create = async () => {};

  // Nastavení
  (Tier as any).getAll = async () => DEMO_TIERS.map((t) => new (Tier as any)(t));
  (Order as any).getOrderManagingUrl = async () => window.location.href;
  (Order as any).create = async () => ({ id: 1 });
  (User as any).prototype.update = async () => {};
};

const DashboardPreview = ({ children }: { children: ReactNode }) => {
  useMemo(installStubs, []);

  // V iframu na HomePage schováme scrollbary (scrollování zůstává funkční).
  // Celostránkové /preview/dashboard si nechává scrollbary normální.
  useEffect(() => {
    if (window.self === window.top) return;
    const style = document.createElement('style');
    style.textContent =
      '* { scrollbar-width: none; -ms-overflow-style: none; } *::-webkit-scrollbar { display: none; }';
    document.head.appendChild(style);
    return () => style.remove();
  }, []);

  const value = useMemo(
    () =>
      ({
        user: mockUser,
        loading: false,
        authStatus: 'authenticated',
        authError: null,
        regError: null,
        setAuthError: noop,
        setRegError: noop,
        onLogin: async () => {},
        onLogout: async () => {},
        onRegister: async () => mockUser,
        onPasswordChange: async () => {},
        onEmailChange: async () => {},
        isLoggedByEmail: () => true,
        setUser: noop,
      }) as any,
    [],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default DashboardPreview;
